// A hundred scored leads from the validation set.
//
// Lifted out of `SpotCheckPage` unchanged so it can appear on the merged Signals
// page as well as on its own route. The columns, the false negative filter and
// the column visibility bag are the ones that ship; the only difference is that
// the page around it now owns the loading.
import { inject, observer } from 'mobx-react';
import React from 'react';
import { Button, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import classNames from 'classnames';

import {
  BehavioralSampleLead,
  CustomerFitSampleLead,
} from '../../../models/SampleLead';
import Store from '../../../store';
import Table from '../generic_components/Table';
import LeadProfileCard from './LeadProfileCard';
import '../trees/segmented.css';
import ModelBaseNames from '../../../models/modelBase/ModelBaseNames';
import './leadSampleTable.css';

export const COLUMN_VISIBILITY_BAG = 'spotCheckColumnVisibility';
export const DEFAULT_COLUMN_VISIBILITY = {
  employees: false,
  amountRaised: false,
  marketCap: false,
  isFortune500Company: false,
  country: false,
  predictiveTraffic: false,
  industry: false,
  hasEnterpriseTechnology: false,
};

type Lead = CustomerFitSampleLead | BehavioralSampleLead;

interface LeadSampleTableProps {
  store?: Store;
  columnVisibility: Partial<Record<keyof CustomerFitSampleLead, boolean>>;
  /** Whether the filter is on. Owned by the page, since it decides the fetch. */
  falseNegativesOnly: boolean;
  onToggleFalseNegatives: () => void;
  onRefresh: () => void;
  /**
   * Called with the group and definition that produced a clicked signal. Absent,
   * the lines render as plain text, which is what the standalone Sample route
   * wants: there is no editor on that page to jump to.
   */
  onJumpToSignal?: (group: number, rule: number) => void;
  /**
   * The tightened rendering: signals as tinted chips, and one Trees column
   * instead of three. Opt in, and only the merged Review page opts in, because
   * the standalone Sample route is the flag off path and has to keep showing what
   * it shows today.
   */
  compact?: boolean;
}

/**
 * One rendered signal: a coloured tick or cross, then the text.
 *
 * The polarity is in the glyph and nowhere else. The spot check SQL writes
 * `REPLACE(REPLACE(direction, '+', '\u2714'), '-', '\u2718')` into the string, and the
 * string is all the front end receives, so a line with neither glyph is drawn
 * without a colour rather than assumed positive.
 *
 * Only the glyph is coloured. Tinting the whole line, which is where this
 * started, made a cell of five signals read as five separate states to resolve
 * rather than as one lead's summary.
 */
function SignalMark({ text }: { text: string }) {
  const positive = text.startsWith('\u2714');
  const negative = text.startsWith('\u2718');
  if (!positive && !negative) return <span className="lst-sig">{text}</span>;
  return (
    <span className="lst-sig">
      <span
        className={positive ? 'lst-sig-yes' : 'lst-sig-no'}
        aria-label={positive ? 'positive signal' : 'negative signal'}
      >
        {positive ? '\u2714' : '\u2718'}
      </span>
      {text.slice(1).trim()}
    </span>
  );
}

function LeadSampleTable({
  store,
  columnVisibility,
  falseNegativesOnly,
  onToggleFalseNegatives,
  onRefresh,
  onJumpToSignal,
  compact,
}: LeadSampleTableProps) {
  const {
    tenant,
    spotCheckPage,
    activeModel: { modelId, type, base },
  } = store;
  // Read in render, so a base switch in this tab redraws the columns.
  const treeBased = base === ModelBaseNames.trees;
  const { leadsSpotCheck, loadingSpotCheck } = spotCheckPage;
  // Cards by default: the page exists to iterate on signals, and that is a
  // question about one lead at a time.
  const [view, setView] = React.useState<'cards' | 'table'>('cards');

  if (!leadsSpotCheck) return null;

  const columnHelper = createColumnHelper<Lead>();

  const idColumn =
    type === 'mqa'
      ? columnHelper.accessor('domain', {
          cell: (info) => info.getValue(),
          header: 'Account',
        })
      : columnHelper.accessor('email', {
          cell: (info) => info.getValue(),
          header: 'Email',
        });

  const treeColumn = (treeNumber: 1 | 2 | 3) =>
    columnHelper.accessor(`tree${treeNumber}Node` as 'tree1Node', {
      cell: (info) => {
        const value = info.getValue();
        return (
          <Link
            to={`/tenant/${tenant}/models/${modelId}/model/trees/${treeNumber}/nodes/${value}`}
          >
            Node {value}
          </Link>
        );
      },
      header: `Tree ${treeNumber}`,
    });

  // One column carrying every tree the lead passed through, instead of three
  // columns that are empty for a single tree model and never compared to each
  // other anyway.
  const treesColumn: ColumnDef<Lead, unknown> = {
    id: 'trees',
    header: 'Trees',
    accessorFn: (lead: CustomerFitSampleLead) =>
      [lead.tree1Node, lead.tree2Node, lead.tree3Node].join(','),
    cell: (info) => {
      const lead = info.row.original as CustomerFitSampleLead;
      const nodes = [lead.tree1Node, lead.tree2Node, lead.tree3Node];
      return (
        <span className="lst-trees">
          {/* Stacked, not side by side. Each row is already several signal lines
              tall, so three buttons down cost no extra height while three across
              cost the column three times its width. */}
          {nodes.map((node, index) =>
            node ? (
              <Link
                key={`tree_${index}`}
                className="lst-tree"
                title={`Tree ${index + 1}, node ${node}`}
                to={`/tenant/${tenant}/models/${modelId}/model/trees/${
                  index + 1
                }/nodes/${node}`}
              >
                <span className="lst-tree-n">T{index + 1}</span>#{node}
              </Link>
            ) : null
          )}
        </span>
      );
    },
  };

  const customerFitColumns: ColumnDef<Lead, unknown>[] = [
    // No tree scores a point based model, so its leads carry no leaves and
    // these columns would be empty on every row.
    ...(!treeBased
      ? []
      : compact
      ? [treesColumn]
      : [treeColumn(1), treeColumn(2), treeColumn(3)]),
    columnHelper.accessor('employees', {
      cell: (info) =>
        info.getValue() && Number(info.getValue()).toLocaleString(),
      header: 'Employees',
    }),
    columnHelper.accessor('amountRaised', {
      cell: (info) =>
        info.getValue() && Number(info.getValue()).toLocaleString(),
      header: 'Amount raised',
    }),
    columnHelper.accessor('marketCap', {
      cell: (info) =>
        info.getValue() && Number(info.getValue()).toLocaleString(),
      header: 'Market cap',
    }),
    {
      accessorFn: ({ isFortune500Company }: CustomerFitSampleLead) =>
        isFortune500Company ? 'yes' : 'no',
      id: 'isFortune500Company',
      header: 'Fortune 500 company',
      cell: (info) => info.getValue(),
    },
    columnHelper.accessor('country', {
      cell: (info) => info.getValue(),
      header: 'Country',
    }),
    columnHelper.accessor('predictiveTraffic', {
      cell: (info) =>
        info.getValue() && Number(info.getValue()).toLocaleString(),
      header: 'Predicted traffic',
    }),
    columnHelper.accessor('industry', {
      cell: (info) => info.getValue(),
      header: 'Industry',
    }),
    {
      accessorFn: ({ hasEnterpriseTechnology }: CustomerFitSampleLead) =>
        hasEnterpriseTechnology ? 'yes' : 'no',
      id: 'hasEnterpriseTechnology',
      header: 'Has entreprise technology',
      cell: (info) => info.getValue(),
    },
  ];

  const columns: ColumnDef<Lead, unknown>[] = [
    idColumn,
    columnHelper.accessor('score', {
      cell: (info) => info.getValue(),
      header: 'Score',
    }),
    columnHelper.accessor('segment', {
      cell: (info) => info.getValue(),
      header: 'Segment',
    }),
    columnHelper.accessor('signals', {
      cell: (info) => {
        // The converter emits one trace per rendered line, in the same order,
        // padding with null where a line could not be attributed, so the line at
        // index N belongs to the trace at index N by construction rather than by
        // the two happening to be filtered the same way. Still matched by text,
        // so a mismatch drops the link rather than pointing at the wrong
        // definition.
        const traces = (info.row.original as CustomerFitSampleLead)
          .signalTraces;
        return (
          <ul className={compact ? 'lst-signals' : 'list-unstyled'}>
            {info
              .getValue()
              .replaceAll('\\n', '\n')
              .split('\n')
              .map((signal, index) => {
                const trace =
                  traces?.[index]?.text === signal ? traces[index] : undefined;
                const content = compact ? <SignalMark text={signal} /> : signal;
                if (!onJumpToSignal || !trace) {
                  return <li key={`${index}-${signal}`}>{content}</li>;
                }
                return (
                  <li key={`${index}-${signal}`}>
                    <button
                      type="button"
                      className="lst-signal"
                      title="Change this signal"
                      onClick={() => onJumpToSignal(trace.group, trace.rule)}
                    >
                      {content}
                      <span className="lst-signal-cue" aria-hidden="true">
                        change
                      </span>
                    </button>
                  </li>
                );
              })}
          </ul>
        );
      },
      header: 'Signals',
    }),
    {
      accessorFn: ({ hasConverted }) => (hasConverted ? 'yes' : 'no'),
      id: 'hasConverted',
      header: 'Has converted',
      cell: (info) => info.getValue(),
    },
  ];
  if (type === 'customer_fit') columns.push(...customerFitColumns);

  return (
    <div className={compact ? 'lst' : ''}>
      {/* Cards answer "does this lead's set of signals make sense", which is one
          lead read whole. The table answers "how does this column behave across
          a hundred leads", which cards cannot do: it owns the sorting, the
          per-column filters and the column picker. Both questions are real, so
          this is a view switch rather than a replacement. */}
      {compact && (
        <div className="lst-views">
          <div className="seg" role="group" aria-label="Sample view">
            <button
              type="button"
              className={`seg-item ${view === 'cards' ? 'seg-item--on' : ''}`}
              aria-pressed={view === 'cards'}
              onClick={() => setView('cards')}
            >
              Cards
            </button>
            <button
              type="button"
              className={`seg-item ${view === 'table' ? 'seg-item--on' : ''}`}
              aria-pressed={view === 'table'}
              onClick={() => setView('table')}
            >
              Table
            </button>
          </div>
          <button
            type="button"
            className="lst-refresh-quiet"
            onClick={onRefresh}
            disabled={loadingSpotCheck}
            title="New sample"
          >
            <i
              className={classNames('fas fa-redo', {
                'fa-spin': loadingSpotCheck,
              })}
            />
          </button>
        </div>
      )}
      <Form.Check
        type="checkbox"
        id="falseNegativesFormCheck"
        label="View only low/medium leads who converted (false negative)"
        name="falseNegativesFormCheck"
        onChange={onToggleFalseNegatives}
        checked={falseNegativesOnly}
      />
      {compact && view === 'cards' ? (
        <div className="lst-cards">
          {(leadsSpotCheck as CustomerFitSampleLead[]).map((lead) => (
            <LeadProfileCard
              key={lead.email}
              lead={lead}
              tenant={tenant}
              modelId={modelId}
              showTrees={treeBased}
              onJumpToSignal={onJumpToSignal}
            />
          ))}
        </div>
      ) : (
        <Table
          data={leadsSpotCheck as Lead[]}
          columns={columns}
          defaultColumnVisibility={
            type === 'customer_fit' ? columnVisibility : null
          }
          columnVisibilityBag={
            type === 'customer_fit' ? COLUMN_VISIBILITY_BAG : null
          }
          additionalButtons={
            <>
              <OverlayTrigger
                overlay={<Tooltip id="reload-spot-check">New sample</Tooltip>}
              >
                <Button
                  className={compact ? 'ms-auto lst-refresh' : 'ms-auto'}
                  variant={compact ? 'light' : undefined}
                  size={compact ? 'sm' : undefined}
                  onClick={onRefresh}
                  disabled={loadingSpotCheck}
                >
                  <i
                    className={classNames('fas fa-redo', {
                      'fa-spin': loadingSpotCheck,
                    })}
                  />
                </Button>
              </OverlayTrigger>
            </>
          }
        />
      )}
    </div>
  );
}

export default inject('store')(observer(LeadSampleTable));
